import React, { FC, useEffect, useRef } from 'react';
import { BodyText, LeadText, Title } from '@gje/typography/lib';
import { getText } from '../../../../services/text/text-service';
import { PrimaryButton } from '@gje/buttons';
import Pair from '../../../../types/Pair';
import useMobile from '../../../../hooks/useMobile';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { HtmlPanel } from '../../../components/HtmlPanel/HtmlPanel';
import ReactToPrint from 'react-to-print';
import {
    createUtagViewData,
    invokeFbqPurchase,
    invokeUtagData
} from '../../../../utils/statisticsUtils';
import {
    resolvePartnerForStats,
    validatePathName
} from '../../../../services/stats/stats-service';
import { StatsDataEnum } from '../../../../types/enums/statsDataEnum';

const lightBulb = require('../../../../images/lightbulb.png');

interface SummaryData {
    sectionsProvider: () => Section[];
}

interface StateToProps {
    calculationData: any;
    calculationOut: any;
    buyingInformationForm: any;
    policyNo: string;
}

interface Section {
    header: string;
    values: Pair<string, string>[];
}

const SummaryStep: FC<StateToProps & SummaryData> = ({
    sectionsProvider,
    policyNo,
    calculationOut
}) => {
    useEffect(() => window.scrollTo(0, 0));
    const isMobile = useMobile();
    const toPrintRef = useRef();
    //Below condition to be removed once e-halsa configured via application portal is in prod
    // let eHalsaRedirectUrl = '/se/1/Mina+sidor/system/e-halsa?policyNo=';
    // if (window.location.host.includes('test')) {
    let eHalsaRedirectUrl = '/kund/privat/system/e-halsa/?policyNo=';
    //}
    useEffect(() => {
        const intervalUtag = setInterval(() => {
            //@ts-ignore
            if (window.utag) {
                invokeUtagData(
                    createUtagViewData(
                        StatsDataEnum.APPLICATION_NAME,
                        'employeeHealthCare',
                        resolvePartnerForStats(),
                        validatePathName(window.location.pathname) +
                            'sjukvardsforsakring/' +
                            StatsDataEnum.PRE_BUY_SUMMARY,
                        StatsDataEnum.PAGE_TITLE,
                        'sjukvardsforsakring',
                        StatsDataEnum.PRE_BUY_SUMMARY
                    )
                );
                clearInterval(intervalUtag);
            }
        }, 500);

        const intervalFbq = setInterval(() => {
            if (window.location.host.includes('dev' || 'test')) {
                clearInterval(intervalFbq);
            }
            //@ts-ignore
            if (window.fbq) {
                invokeFbqPurchase(
                    'sjukvardsforsakring',
                    calculationOut.premiumList[0].premium
                );
                clearInterval(intervalFbq);
            }
        }, 500);
    }, []);

    const renderCommonHeadSection = () => (
        <>
            <Title
                tag="h2"
                className={classNames(
                    'row summary-page-row-padding',
                    'padding-top-bottom-39'
                )}>
                {getText(
                    'webshopsweden.products.employeeHealthcare.summaryPage.header'
                )}
            </Title>
            <BodyText className="row padding-bottom-39 summary-page-row-padding small-padding-bottom-print">
                <HtmlPanel>
                    {getText(
                        'webshopsweden.products.employeeHealthcare.summaryPage.headerText'
                    )}
                </HtmlPanel>
            </BodyText>
        </>
    );

    const renderCommonFooterSection = () => (
        <>
            <div className="row summary-page-light-bubble-bg display-inline-flex align-items summary-page-row-padding print-bulb w-100 before-you-start-section-padding hide-in-print margin-top-13">
                <div className="align-self-start">
                    <img
                        id={'lightbulb'}
                        src={lightBulb}
                        alt="lightbulb"
                        width="50px"
                        height="50px"
                    />
                </div>
                <div>
                    <Title tag="h3">
                        {getText('webshopsweden.common.note')}
                    </Title>
                    <LeadText className={'margin-bottom-13'}>
                        {getText(
                            'webshopsweden.products.employeeHealthcare.summaryPage.instruction'
                        )}
                    </LeadText>
                    <LeadText>
                        {getText(
                            'webshopsweden.products.employeeHealthcare.summaryPage.instruction.footer'
                        )}
                    </LeadText>
                </div>
            </div>
            <div className="row padding-top-bottom-39 healthcare-summary-row-padding small-padding-bottom-print">
                <LeadText>
                    <div className="no-padding-left col-sm-23">
                        {getText(
                            'webshopsweden.products.accident.summaryPage.14daysCancellation'
                        )}
                    </div>
                </LeadText>
            </div>
            <div className="row padding-top-39 margin-left-50 healthcare-summary-row-padding small-padding-bottom-print w-66-print no-padding-right-print hide-in-print">
                <div
                    className={`${
                        isMobile
                            ? 'no-padding-left'
                            : 'healthcare-button-col-padding'
                    } col-sm-8 margin-bottom-39 no-margin-bottom-print small-padding-bottom-print up-buttons-width-print hide-in-print`}>
                    <PrimaryButton
                        borderColor="red"
                        flexible={true}
                        onClick={() => {
                            window.open(
                                `${eHalsaRedirectUrl}${policyNo}`,
                                '_blank'
                            );
                        }}>
                        <Title
                            tag={'h4'}
                            children={getText(
                                'webshopsweden.products.employeeHealthcare.summaryPage.submitButton'
                            )}
                        />
                    </PrimaryButton>
                </div>
                <div className="col-sm-6 no-padding-left margin-bottom-39 no-margin-bottom-print small-padding-bottom-print up-buttons-width-print">
                    <ReactToPrint
                        trigger={() => (
                            <PrimaryButton
                                borderColor="default"
                                flexible={true}
                                asLink={true}>
                                <Title
                                    tag={'h4'}
                                    children={getText(
                                        'webshopsweden.common.summaryPage.buttons.print'
                                    )}
                                />
                            </PrimaryButton>
                        )}
                        content={() => toPrintRef.current}
                    />
                </div>
            </div>
            <div
                className={
                    'summary-page-row-padding healthcare-summary-row-padding margin-top-39'
                }>
                <div>
                    <p>
                        {getText(
                            'webshopsweden.products.employeeHealthcare.summaryPage.contactInfo'
                        )}
                    </p>
                </div>
            </div>
        </>
    );

    return (
        <div className="row" ref={toPrintRef}>
            {renderCommonHeadSection()}
            {sectionsProvider().map(s => (
                <>
                    <Title
                        tag="h2"
                        className="row padding-top-bottom-39 margin-left-50 summary-page-row-padding">
                        {s.header}
                    </Title>
                    {s.values.map(p => renderUnderlinedField(p.key, p.value))}
                </>
            ))}
            {renderCommonFooterSection()}
        </div>
    );
};

const renderUnderlinedField = (key: string, value: string) => {
    return (
        <div
            className={`row healthcare-summary-row-padding margin-bottom-26 margin-left-30 display-flex align-items-bottom small-margin-bottom-print`}>
            <Title
                tag="h5"
                className="col-xs-12 summary-page-underlined-element">
                {key}
            </Title>
            <Title
                tag="h5"
                className="col-xs-11 text-align-right summary-page-underlined-element">
                {value}
            </Title>
        </div>
    );
};

const mapStateToProps = state => ({
    calculationData: state.calculationData.calculatedData.calculation,
    calculationOut: state.calculationData.calculatedData.calculationOut,
    buyingInformationForm: state.form.buyingInformation,
    policyNo: state.calculationData.calculatedData.calculationOut.policyNumber
});

export default connect<StateToProps>(mapStateToProps, {})(SummaryStep);
